import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { UiItemContext } from '../../../cnr/contexts/UiItemContext';
import { sortObject } from '../../../common/sorting';
import UiSaveButtons from '../../../components/buttons/UiSaveButtons';
import { appIconTypes } from '../../../enums/appIconTypes';
import GenericIcon from '../../../genericControls/GenericIcon';
import { createWizardGroups } from '../../../viewSettings/helpers/wizard';
import FullPageWrapper from '../../../wrappers/FullPageWrapper';
import { H3Topper } from '../../../wrappers/TopperWrappers';
import Wrapper, { wrapperTypes } from '../../../wrappers/Wrapper';
import FormForm from '../FormForm';
import { getFormPropz } from '../props/formPropz';
import WrapperVertCols from 'global/wrappers/WrapperVertCols';

/**
 * 
 * @param {object} props 
 * @param {object} - form_handlers: group of handler functions
 * @param {function} - handlePreview: function to handle the preview
 * @param {function} - handleSave: data function to handle the save
 * @param {boolean} - noModal: set if the component is in modal mode
 * @param {boolean} - topperCaption: caption for the top of the wizard
 * @param {boolean} - topperCaption2: caption for the sub top of the wizard
 * @param {object} - viewProps: group of viewProps
 * @description This will take the normal appForm and turn it into a wizard. It will break the viewProps into wizardGroup.
 * @returns StepWizard
 */
export const AppFormWizard = (props) => {

  const {
    form_handlers,
    handlePreview,
    handleSave,
    handleSaveCaption,
    noModal,
    topperCaption,
    topperCaption2,
    updating,
    viewProps,
    useMvc,
  } = props

  // uiItemContext
  const uiItemContext = useContext(UiItemContext);
  const { item_handlers } = uiItemContext ?? {}

  const handleCancel = () => {
    item_handlers && item_handlers.handleShow_itemSidebar && item_handlers.handleShow_itemSidebar()
    props.handleCancel && props.handleCancel()
  }

  // local state  
  const [step, setStep] = useState(0)
  const [formData, setFormData] = useState(props.formData)
  const [wizardGroups, setWizardGroups] = useState()
  const [wizardForms, setWizardForms] = useState()
  const [currentForm, setCurrentForm] = useState()

  const stepCount = wizardForms ? Object.keys(wizardForms).length : 0

  /** Creates the forms for the wizard */
  const getForms = (_wizardGroups) => {
    if (_.size(_wizardGroups) > 0) {
      const frms = []
      _.forEach(_wizardGroups, (wizardGroup, wgKey) => {
        const fpp = { form_handlers, viewProps: wizardGroup, data_init: formData }
        const fprs = getFormPropz(fpp)
        frms.push({
          name: wgKey,
          props: {
            formAttributes: { className: "form-" + wgKey },
            formProps: fprs,
            isWizard: true,
            formKey: wgKey,
          },
        })
      })
      setWizardGroups(_wizardGroups)
      setWizardForms(frms)
      setCurrentForm(frms[0]) // pick first
    }
  }

  useEffect(() => {
    if (viewProps) {
      const sortedProps = sortObject(viewProps, '_position')
      const _wizardGroups = createWizardGroups(sortedProps)
      getForms(_wizardGroups)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  useEffect(() => {
    setFormData(props.formData)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [props.formData]);

  const getCurrentForm = (newStep) => {
    const cf = wizardForms[newStep]
    setCurrentForm(cf)
    setStep(newStep)
  }

  const validateCurrentForm = () => {
    let itemIsReady = true
    const wizardGroup = wizardGroups[currentForm.name]
    Object.keys(wizardGroup).forEach(key => {
      if (itemIsReady) {
        const itemProp = wizardGroup[key]
        if (itemProp && itemProp.data && itemProp.data.required) {
          if (formData) {
            if (!formData[key] || formData[key] === '') {
              itemIsReady = false
            }
          }
        }
      }
    })
    return itemIsReady
  }

  const validateForm = () => {
    let formIsReady = true
    _.forEach(wizardGroups, (wizardGroup, wgKey) => {
      _.forEach(wizardGroup, (itemProp, itemPropKey) => {
        if (formIsReady) {
          if (itemProp && itemProp.data && itemProp.data.required) {
            if (formData) {
              if (!formData[itemPropKey] || formData[itemPropKey] === '') {
                formIsReady = false
              }
            }
          }
        }
      })
    })
    return formIsReady
  }

  const allowBack = step > 0
  const allowForward = (step < (stepCount - 1) && validateCurrentForm())
  const allowSave = wizardGroups ? validateForm() : false

  const handleStep = (co) => {
    const { count } = co
    let newStep = step + count
    if (newStep < 0) { newStep = 0 }
    if (newStep > (stepCount - 1)) { newStep = stepCount - 1 }
    getCurrentForm(newStep)
  }

  const gi = (key, iconName, onClick, clickOptions, opts, corner) => <GenericIcon giProps={{ key, iconName, onClick, clickOptions, opts, corner }} />

  const buttonProps = { color: 'black', inverted: true }

  const backButton = () => gi('btn-back', appIconTypes.arrowLeft, handleStep, { count: -1 }, { ...buttonProps, nb: false, nf: true, disabled: !allowBack })
  const nextButton = () => gi('btn-forward', appIconTypes.arrowRight, handleStep, { count: 1 }, { ...buttonProps, nb: false, nf: true, disabled: !allowForward })

  const header = () => <H3Topper
    leftContent={backButton()}
    middleContent={currentForm ? _.startCase(currentForm.name) : '-'}
    rightContent={nextButton()}
  />

  const footer = () => <UiSaveButtons
    preview={{ oc: handlePreview, disabled: !allowSave }}
    save={{ oc: handleSave, disabled: !allowSave, caption: handleSaveCaption ? handleSaveCaption : 'Save' }}
  ></UiSaveButtons>

  const form = (formProps, key) => <FormForm
    formAttributes={{ className: 'form-' + key }}
    formProps={formProps}
    isWizard={true}
    formKey={key}
  />

  const currForm = () => form(currentForm?.props?.formProps, currentForm.name)

  const allForms = () => {
    return _.map(wizardForms, (wizardForm, wizardFormKey) => (
      <div style={{ display: wizardForm.name === currentForm?.name ? 'block' : useMvc ? 'block' : 'none' }}>
        {form(wizardForm?.props?.formProps, wizardForm.formKey)}
      </div>
    ))
  }

  // const filterWithValues = (input) => {
  //   if (_.isArray(input)) {
  //     return _.chain(input)
  //       .map(item => filterWithValues(item))   // recurse
  //       .filter(item => !_.isEmpty(item))      // remove empty entries
  //       .value();
  //   }

  //   if (_.isObject(input)) {
  //     return _.chain(input)
  //       .omit(["data_init"]) // 🚫 remove data_init
  //       .pickBy((value, key) => {
  //         if (_.isArray(value) || _.isObject(value)) {
  //           return !_.isEmpty(filterWithValues(value));
  //         }
  //         return !_.isNil(value) && value !== "";
  //       })
  //       .mapValues(value => (_.isObject(value) ? filterWithValues(value) : value))
  //       .value();
  //   }

  //   return input;
  // };


  const allForms_mvc = () => {
    console.log('wizardForms', wizardForms)
    // const sss = filterWithValues(wizardForms)
    // console.log(JSON.stringify(filterWithValues(sss), null, 2));
    return _.map(wizardForms, (wizardForm, wizardFormKey) => (
      <Wrapper
        header={wizardForm.formKey}
        content={form(wizardForm?.props?.formProps, wizardForm.formKey)}
      ></Wrapper>
    ))
  }

  const mvc = () => <WrapperVertCols mvc={_.size(wizardForms)}>
    {allForms_mvc()}
  </WrapperVertCols>

  const wrapper = () => <Wrapper
    header={header()}
    content={allForms()}
    footer={(handlePreview || handleSave) && footer()}
    wrapperType={wrapperTypes.paddedFooter}
    updating={updating}
  />

  const fullPageWrapper = () => <FullPageWrapper
    content={wrapper()}
    handleCancel={handleCancel}
    topperCaption={topperCaption}
    topperCaption2={topperCaption2}
    noModal={noModal}
    updating={updating}
  />

  if (useMvc) {
    return mvc()
  } else {
    return currentForm ? wrapper() : <div></div>
  }


} 